import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
	Component,
	input,
	output,
	ViewChild,
	inject,
	signal,
	computed,
	effect,
	OnDestroy,
	OnInit,
	PLATFORM_ID,
	Inject
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
	StripeService,
	StripeElementsDirective,
	StripePaymentElementComponent,
	StripeAddressComponent
} from 'ngx-stripe';
import { StripeAddressElementOptions, StripeElementsOptions, StripeElements } from '@stripe/stripe-js';
import { Subscription } from 'rxjs';
import { StripeCheckoutService } from '@sp-client/app/services/stripe-checkout.service';
import { PlanService } from '@sp-client/app/services/plan.service';
import { UserService } from '@sp-client/app/services/user.service';
import { ApiService } from '@sp-client/app/services/api.service';
import { LogService } from '@sp-client/app/services/log.service';
import { StoreService } from '@sp-client/app/services/store.service';
import { ButtonComponent } from '../button/button.component';
import { ImageComponent } from '../image/image.component';
import { RadioButtonListComponent } from '../radio-button-list/radio-button-list.component';
import { RadioButtonOption } from '@sp-client/app/interfaces/radio-button-option.interface';
import { cm } from '@sp-shared';
import { PillComponent } from '../pill/pill.component';
import { ProperTitleCasePipe } from '@sp-client/app/pipes/proper-title-case.pipe';
import { ProgressDotsComponent } from '@sp-client/app/components/progress-dots/progress-dots.component';

// Receipt data structure
interface ReceiptData {
	orderNumber?: string | null;
	name: string;
	billingAddress: {
		line1?: string;
		line2?: string;
		city?: string;
		state?: string;
		postal_code?: string;
		country?: string;
	};
	cardLast4?: string;
	subtotal: number;
	tax: number;
	total: number;
	currency: string;
}

@Component({
	selector: 'sp-store-select-plan',
	imports: [
		CommonModule,
		ReactiveFormsModule,
		ButtonComponent,
		ImageComponent,
		RadioButtonListComponent,
		PillComponent,
		ProperTitleCasePipe,
		ProgressDotsComponent
	],
	templateUrl: './store-select-plan.component.html',
	styleUrl: './store-select-plan.component.scss'
})
export class StoreSelectPlanComponent implements OnInit, OnDestroy {
	@ViewChild(StripePaymentElementComponent) paymentElement!: StripePaymentElementComponent;

	readonly amount = input<number>(0);
	readonly currency = input<string>('usd');
	readonly description = input<string>('');
	readonly disabled = input<boolean>(false);
	readonly loading = input<boolean>(false);
	readonly initialSelectedProduct = input<string>('');

	readonly stripeService = inject(StripeService);
	private readonly stripeCheckoutService = inject(StripeCheckoutService);
	private readonly planService = inject(PlanService);
	private readonly userService = inject(UserService);
	private readonly apiService = inject(ApiService);
	private readonly log = inject(LogService);
	private readonly formBuilder = inject(FormBuilder);
	private readonly storeService = inject(StoreService);

	// Local signal for two-way binding with radio button component
	// This syncs with the service
	private _selectedProduct = signal<string>('');
	selectedProduct = this._selectedProduct;

	// Computed to read from service (for other components to access)
	selectedProductFromService = computed(() => this.storeService.getSelectedProductValueSignal()());
	hasValidationError = signal<boolean>(false);

	checkoutForm: FormGroup;
	private paymentTypeSubscription?: Subscription;
	private hasInitialized = signal<boolean>(false);

	checkoutInitiated = output<void>();
	checkoutCompleted = output<unknown>();
	checkoutError = output<Error>();
	proceedToCheckout = output<string>(); // Emits selectedProduct when user clicks "Continue to Payment"

	constructor(@Inject(PLATFORM_ID) private platformId: object) {
		this.checkoutForm = this.formBuilder.group({
			paymentType: ['one-time', Validators.required]
		});

		// Sync form with selectedPaymentType signal
		this.paymentTypeSubscription = this.checkoutForm.get('paymentType')?.valueChanges.subscribe((value: string) => {
			if (value === 'one-time' || value === 'subscription') {
				if (value !== this.selectedPaymentType()) {
					this.selectedPaymentType.set(value);
				}
			}
		});

		// Sync initialSelectedProduct input with service and local signal
		effect(() => {
			const initialValue = this.initialSelectedProduct();
			if (initialValue) {
				this.storeService.setSelectedProduct(initialValue);
				this._selectedProduct.set(initialValue);
			}
		});

		// Sync local signal changes to service (when user selects product)
		effect(() => {
			const localValue = this._selectedProduct();
			const serviceValue = this.storeService.getSelectedProductValueSignal()();
			if (localValue !== serviceValue) {
				this.storeService.setSelectedProduct(localValue);
			}
		});

		// Sync service changes to local signal (when service is updated externally)
		effect(() => {
			const serviceValue = this.storeService.getSelectedProductValueSignal()();
			const localValue = this._selectedProduct();
			if (serviceValue !== localValue) {
				this._selectedProduct.set(serviceValue);
			}
		});
	}

	ngOnInit(): void {
		// Initialize selectedProduct with initialSelectedProduct if provided
		const initialValue = this.initialSelectedProduct();
		if (initialValue) {
			this.storeService.setSelectedProduct(initialValue);
			this._selectedProduct.set(initialValue);
		}

		// Check for Stripe redirect parameters in URL
		// When Stripe redirects for 3D Secure, it returns with payment_intent and redirect_status in the URL
		// Only run in browser environment
		if (isPlatformBrowser(this.platformId)) {
			void this.handleStripeRedirect();
		}
	}

	ngOnDestroy(): void {
		this.paymentTypeSubscription?.unsubscribe();
	}

	get productOptions(): RadioButtonOption[] {
		return this.storeService.getProductOptions();
	}

	handleError(hasError: boolean): void {
		if (hasError) {
			this.log.warn('Product selection validation failed');
			// Could show a toast notification or error message here
		} else {
			// Clear validation error when a product is selected
			this.hasValidationError.set(false);
		}
	}

	handleClose(): void {
		this.checkoutCompleted.emit({ done: true });
	}

	/**
	 * Handles Stripe redirect after 3D Secure authentication
	 * Checks URL parameters for payment_intent and redirect_status
	 */
	private async handleStripeRedirect(): Promise<void> {
		// Only run in browser environment
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}

		const urlParams = new URLSearchParams(window.location.search);
		const paymentIntentId = urlParams.get('payment_intent');
		const redirectStatus = urlParams.get('redirect_status');

		if (paymentIntentId && redirectStatus) {
			this.log.debug('Stripe redirect detected', {
				paymentIntentId,
				redirectStatus
			});

			// If payment succeeded, complete the unlock flow
			if (redirectStatus === 'succeeded') {
				this.log.info('Payment succeeded after redirect, unlocking plan', {
					paymentIntentId
				});

				// Set processing state
				this.isProcessingPayment.set(true);
				this.errorMessage.set(null);

				try {
					const plan = this.selectedPlan();
					if (!plan?.id) {
						this.log.warn('No plan available to unlock after redirect');
						// Clean up URL parameters
						this.cleanupUrl();
						return;
					}

					// Verify payment intent and unlock plan
					const verificationResult = await this.apiService.verifyPaymentIntent(paymentIntentId, plan.id);

					this.log.debug('Payment verification result after redirect', {
						success: verificationResult.success,
						planId: verificationResult.plan?.id,
						purchaseLock: verificationResult.plan?.purchaseLock
					});

					if (verificationResult.success && verificationResult.plan) {
						const unlockedPlan = verificationResult.plan;

						if (unlockedPlan.purchaseLock === false) {
							// Update local selected plan signal with unlocked plan
							this.planService.selectedPlanSignal.set(unlockedPlan);
							this.planService.setPlanIsLocked(false);

							this.log.debug('Plan unlocked after redirect', {
								planId: unlockedPlan.id,
								purchaseLock: unlockedPlan.purchaseLock
							});

							// Refresh full plan data
							setTimeout(() => {
								void (async () => {
									try {
										await this.planService.getFullPlanData(plan.id);
										const refreshedPlan = this.planService.selectedPlanSignal();
										if (refreshedPlan && refreshedPlan.purchaseLock === false) {
											this.log.debug('Plan refreshed and confirmed unlocked after redirect', {
												planId: refreshedPlan.id,
												purchaseLock: refreshedPlan.purchaseLock
											});
										}
									} catch (refreshError) {
										this.log.error('Error refreshing plan after redirect:', refreshError);
									}
								})();
							}, 1000);

							// Ensure receipt data is set before showing success
							// Use orderNumber from verification result or paymentIntentId as fallback
							const orderNumber: string | null = verificationResult.orderNumber || paymentIntentId || null;
							this.ensureReceiptData(orderNumber);

							// Set success state and emit success event
							this.paymentSucceeded.set(true);
							this.checkoutCompleted.emit({ paymentIntent: { id: paymentIntentId, status: 'succeeded' } });
						}
					}

					// Clean up URL parameters
					this.cleanupUrl();
				} catch (error) {
					this.log.error('Error handling Stripe redirect:', error);
					const errorMsg = error instanceof Error ? error.message : 'Error processing payment after redirect.';
					this.errorMessage.set(errorMsg);
					this.checkoutError.emit(error instanceof Error ? error : new Error(errorMsg));
				} finally {
					this.isProcessingPayment.set(false);
				}
			} else if (redirectStatus === 'failed') {
				// Payment failed after redirect
				this.log.warn('Payment failed after redirect', { paymentIntentId });
				this.errorMessage.set('Payment failed. Please try again.');
				this.checkoutError.emit(new Error('Payment failed after redirect'));
				this.cleanupUrl();
			}
		}
	}

	/**
	 * Removes Stripe redirect parameters from URL
	 */
	private cleanupUrl(): void {
		// Only run in browser environment
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}

		const url = new URL(window.location.href);
		url.searchParams.delete('payment_intent');
		url.searchParams.delete('redirect_status');
		url.searchParams.delete('payment_intent_client_secret');
		// Update URL without page reload
		window.history.replaceState({}, '', url.toString());
	}

	// Payment type selection
	selectedPaymentType = signal<'one-time' | 'subscription'>('one-time');

	// Client secret state
	clientSecret = signal<string | null>(null);
	isLoadingClientSecret = signal<boolean>(false);
	errorMessage = signal<string | null>(null);
	isProcessingPayment = signal<boolean>(false);
	paymentSucceeded = signal<boolean>(false);
	receiptData = signal<ReceiptData | null>(null);

	// Current step for progress dots - always step 0 for stripe-select-plan component
	currentStep = computed(() => {
		// Stripe-select-plan is always step 0 (first step)
		return 0;
	});

	// Selected plan
	selectedPlan = computed(() => this.planService.selectedPlanSignal());

	// Country name from selected plan
	countryName = computed(() => this.selectedPlan()?.countryName || null);

	// Country name with spaces replaced by hyphens (for image filenames)
	countryNameImage = computed(() => {
		const name = this.selectedPlan()?.countryName;
		return name ? name.replace(/\s+/g, '-') : null;
	});

	// User data
	user = computed(() => this.userService.userSignal());

	// Full name from user data
	fullName = computed(() => {
		const user = this.user();
		if (user?.firstName && user?.lastName) {
			return `${user.firstName} ${user.lastName}`;
		}
		if (user?.firstName) {
			return user.firstName;
		}
		if (user?.lastName) {
			return user.lastName;
		}
		return null;
	});

	// Computed signals for button states
	isOneTimeSelected = computed(() => this.selectedPaymentType() === 'one-time');
	isSubscriptionSelected = computed(() => this.selectedPaymentType() === 'subscription');

	// Subscription monthly price
	private readonly subscriptionMonthlyPrice = 29;

	// Tax rate (8% - can be made configurable)
	private readonly taxRate = 0.08;

	// Computed signal for display amount based on payment type
	displayAmount = computed(() => {
		if (this.selectedPaymentType() === 'subscription') {
			return this.subscriptionMonthlyPrice;
		}
		// One-time payment uses the amount input
		return this.amount();
	});

	// Price breakdown computed signals
	subtotal = computed(() => this.displayAmount());
	tax = computed(() => Math.round(this.subtotal() * this.taxRate * 100) / 100);
	total = computed(() => this.subtotal() + this.tax());

	// Computed signal for payment type description
	paymentTypeDescription = computed(() => {
		if (this.selectedPaymentType() === 'one-time') {
			return '12 month access to Scoutly Plans service';
		}
		return 'Monthly payment of $29';
	});

	// Elements options with client secret
	elementsOptions = computed<StripeElementsOptions>(() => {
		const options: StripeElementsOptions = {
			locale: 'en',
			// Enable appearance customization for better test card helper visibility
			appearance: {
				theme: 'stripe' as const
			}
		};

		const secret = this.clientSecret();
		if (secret) {
			options.clientSecret = secret;
		}

		return options;
	});

	billingAddressOptions = computed<StripeAddressElementOptions>(() => {
		// Create a stable options object - mode cannot be changed after element creation
		const options: StripeAddressElementOptions = {
			mode: 'billing'
		};

		const name = this.fullName();
		if (name) {
			options.defaultValues = {
				name: name
			};
		}

		// Return a new object reference only when name changes, not on every computation
		// This prevents Stripe from trying to update the mode
		return { ...options };
	});

	/**
	 * Selects payment type and fetches client secret
	 */
	async selectPaymentType(type: 'one-time' | 'subscription'): Promise<void> {
		// Prevent multiple simultaneous requests
		if (this.isLoadingClientSecret()) {
			return;
		}

		// If we already have a client secret for this payment type, don't fetch again
		const currentType = this.selectedPaymentType();
		const currentSecret = this.clientSecret();
		if (currentSecret && currentType === type) {
			return;
		}

		// Check if plan is available before proceeding
		const plan = this.selectedPlan();
		if (!plan?.id) {
			this.errorMessage.set('Please select a plan to continue.');
			return;
		}

		// Only update payment type if it's different
		if (currentType !== type) {
			this.selectedPaymentType.set(type);
			// Use emitEvent: false to prevent triggering valueChanges subscription
			// since we're already manually setting the signal above
			this.checkoutForm.patchValue({ paymentType: type }, { emitEvent: false });
		}

		this.errorMessage.set(null);
		this.isLoadingClientSecret.set(true);

		// Only clear client secret if switching payment types
		if (currentType !== type) {
			this.clientSecret.set(null);
		}

		try {
			const planId = this.stripeCheckoutService.validatePlanId(plan);

			const secret = await this.stripeCheckoutService.getClientSecret(type, planId);
			this.clientSecret.set(secret);
			this.hasInitialized.set(true);
		} catch (error: unknown) {
			const errorMsg = error instanceof Error ? error.message : 'Failed to initialize payment. Please try again.';
			this.errorMessage.set(errorMsg);
			this.checkoutError.emit(error instanceof Error ? error : new Error(errorMsg));
		} finally {
			this.isLoadingClientSecret.set(false);
		}
	}

	/**
	 * Handles checkout/payment confirmation
	 * When used in modal, emits proceedToCheckout event to show stripe-checkout component
	 */
	async handleCheckout(): Promise<void> {
		// Validate product selection
		if (cm.isEmpty(this.selectedProduct())) {
			this.hasValidationError.set(true);
			this.log.warn('Product selection validation failed - no product selected');
			return;
		}

		// Clear any previous validation errors
		this.hasValidationError.set(false);

		// Emit event to proceed to checkout component (for modal usage)
		this.proceedToCheckout.emit(this.selectedProduct());

		// If clientSecret exists, continue with payment processing (for non-modal usage)
		if (this.disabled() || this.loading() || this.isProcessingPayment() || !this.clientSecret()) {
			return;
		}

		if (!this.checkoutForm.valid) {
			this.errorMessage.set('Please complete all required fields.');
			return;
		}

		this.checkoutInitiated.emit();
		this.isProcessingPayment.set(true);
		this.errorMessage.set(null);
		this.log.debug('Payment processing started - isProcessingPayment set to true');

		try {
			// Get elements from StripePaymentElementComponent
			// The paymentElement component exposes the elements instance
			if (!this.paymentElement?.elements) {
				throw new Error('Payment form is not ready. Please wait a moment and try again.');
			}

			const elements = this.paymentElement.elements;

			const secret = this.clientSecret();

			if (!secret) {
				throw new Error('Payment session expired. Please select a payment type again.');
			}

			// Extract user information
			const user = this.user();
			const userEmail = user?.emailAddress || null;
			const userName = this.fullName();

			// Confirm payment with Stripe, passing user information
			const result = await this.stripeCheckoutService.confirmPaymentWithStripe(
				this.stripeService,
				elements,
				secret,
				userName,
				userEmail
			);

			// Extract payment intent ID and status from result
			const plan = this.selectedPlan();
			if (!plan?.id) {
				this.log.warn('No plan available to unlock after payment');
				const successResult = this.stripeCheckoutService.handlePaymentSuccess(result);
				this.checkoutCompleted.emit(successResult);
				// Don't return early - let finally block handle isProcessingPayment
				return;
			}

			// Extract payment intent ID and status from result
			let paymentIntentId: string | undefined;
			let paymentIntentStatus: string | undefined;

			// Log the full result structure to debug
			this.log.debug('Full payment confirmation result', {
				result,
				resultType: typeof result,
				resultKeys: result && typeof result === 'object' ? Object.keys(result as Record<string, unknown>) : []
			});

			// Try to extract from result object first (most reliable)
			if (result && typeof result === 'object') {
				const resultObj = result as unknown as Record<string, unknown>;

				// Check for paymentIntent in result
				const paymentIntent = resultObj['paymentIntent'] as Record<string, unknown> | undefined;
				if (paymentIntent) {
					paymentIntentId = paymentIntent['id'] as string | undefined;
					paymentIntentStatus = paymentIntent['status'] as string | undefined;
					this.log.debug('Extracted from paymentIntent object', {
						id: paymentIntentId,
						status: paymentIntentStatus,
						paymentIntentKeys: Object.keys(paymentIntent)
					});
				}

				// Also check if result itself has id and status (some Stripe responses are structured this way)
				if (!paymentIntentId && resultObj['id']) {
					paymentIntentId = resultObj['id'] as string | undefined;
					paymentIntentStatus = resultObj['status'] as string | undefined;
					this.log.debug('Extracted from result object directly', {
						id: paymentIntentId,
						status: paymentIntentStatus
					});
				}
			}

			// Fallback: extract from client secret (format: pi_xxx_secret_yyy)
			if (!paymentIntentId) {
				const clientSecretValue = this.clientSecret();
				if (clientSecretValue) {
					const secretParts = clientSecretValue.split('_secret_');
					if (secretParts.length > 0 && secretParts[0].startsWith('pi_')) {
						paymentIntentId = secretParts[0];
						this.log.debug('Extracted payment intent ID from client secret', { paymentIntentId });
					}
				}
			}

			this.log.debug('Payment confirmation result summary', {
				paymentIntentId,
				paymentIntentStatus,
				hasResult: !!result,
				willUnlock: paymentIntentId && paymentIntentStatus === 'succeeded'
			});

			// Verify and unlock if we have a payment intent ID
			// If status is 'succeeded', unlock immediately
			// If status is undefined or not 'succeeded', still try to verify (backend will check actual status)
			if (paymentIntentId) {
				// If status is explicitly 'succeeded', unlock immediately
				if (paymentIntentStatus === 'succeeded') {
					this.log.debug('Payment succeeded (from result), unlocking plan', {
						paymentIntentId,
						planId: plan.id,
						status: paymentIntentStatus
					});
				} else {
					// Status is undefined or not 'succeeded' - still verify on backend
					// Backend will check the actual status in Stripe
					this.log.debug('Payment status unclear from result, verifying on backend', {
						paymentIntentId,
						planId: plan.id,
						statusFromResult: paymentIntentStatus
					});
				}

				try {
					// Call API to verify payment intent and unlock plan on server
					// This sets purchaseLock = false and saves to Firestore via backend unlockPlan -> updatePlan
					// The backend will check the actual payment intent status in Stripe
					const verificationResult = await this.apiService.verifyPaymentIntent(paymentIntentId, plan.id);

					this.log.debug('Payment verification result', {
						success: verificationResult.success,
						planId: verificationResult.plan?.id,
						purchaseLock: verificationResult.plan?.purchaseLock
					});

					if (verificationResult.success && verificationResult.plan) {
						// Update local selected plan signal with unlocked plan (purchaseLock = false)
						const unlockedPlan = verificationResult.plan;

						// Verify the plan is actually unlocked
						if (unlockedPlan.purchaseLock === false) {
							// Set the unlocked plan - the effect in PlanService will automatically update planIsLockedSignal
							this.planService.selectedPlanSignal.set(unlockedPlan);

							// Explicitly ensure the locked signal is set to false
							// The effect should handle this, but we do it explicitly to ensure it updates immediately
							this.planService.setPlanIsLocked(false);

							this.log.debug('Plan unlocked and local state updated', {
								planId: unlockedPlan.id,
								purchaseLock: unlockedPlan.purchaseLock,
								planIsLocked: this.planService.planIsLockedSignal()
							});

							// Wait a moment for backend to fully save to Firestore, then refresh full plan data
							// This ensures we get the complete plan structure with ordered guides
							setTimeout(() => {
								void (async () => {
									try {
										await this.planService.getFullPlanData(plan.id);
										// Verify the refreshed plan is still unlocked
										const refreshedPlan = this.planService.selectedPlanSignal();
										if (refreshedPlan) {
											if (refreshedPlan.purchaseLock === false) {
												this.log.debug('Plan refreshed and confirmed unlocked', {
													planId: refreshedPlan.id,
													purchaseLock: refreshedPlan.purchaseLock,
													planIsLocked: this.planService.planIsLockedSignal()
												});
											} else {
												this.log.warn('Plan was locked again after refresh, re-unlocking', {
													planId: refreshedPlan.id,
													purchaseLock: refreshedPlan.purchaseLock
												});
												// Re-unlock the plan if it got locked again (backend might not have saved yet)
												refreshedPlan.purchaseLock = false;
												this.planService.selectedPlanSignal.set(refreshedPlan);
												this.planService.setPlanIsLocked(false);
											}
										}
									} catch (refreshError) {
										this.log.error('Error refreshing plan after unlock:', refreshError);
									}
								})();
							}, 1000);

							// Extract receipt data before showing success
							// Always set receipt data, even if extraction fails
							const orderNumber: string | null = verificationResult.orderNumber || paymentIntentId || null;
							try {
								await this.extractReceiptData(result, elements, orderNumber);
							} catch (receiptError) {
								this.log.error('Error extracting receipt data, using fallback:', receiptError);
								this.ensureReceiptData(orderNumber);
							}

							// Ensure receipt data is set (fallback if extraction didn't set it)
							this.ensureReceiptData(orderNumber);

							// Set success state - this will show the success message in the modal
							this.paymentSucceeded.set(true);
							const successResult = this.stripeCheckoutService.handlePaymentSuccess(result);
							// this.checkoutCompleted.emit(successResult);
						} else {
							this.log.warn('Plan returned from verification still has purchaseLock = true', {
								planId: unlockedPlan.id,
								purchaseLock: unlockedPlan.purchaseLock
							});
							// Still emit success even if unlock failed - webhook will handle it
							const successResult = this.stripeCheckoutService.handlePaymentSuccess(result);
							// this.checkoutCompleted.emit(successResult);
						}
					} else {
						this.log.warn('Payment verification returned unsuccessful', {
							// success: verificationResult.success,
							planId: plan.id
						});
						// Still emit success - payment went through, verification might be delayed
						const successResult = this.stripeCheckoutService.handlePaymentSuccess(result);
						// this.checkoutCompleted.emit(successResult);
					}
				} catch (unlockError) {
					// Log error but don't fail the payment - webhook will handle it
					this.log.error('Error unlocking plan after payment:', unlockError);
					// Still emit success - payment went through
					const successResult = this.stripeCheckoutService.handlePaymentSuccess(result);
					// this.checkoutCompleted.emit(successResult);
				}
			} else {
				this.log.warn('Could not extract payment intent ID from result or client secret', {
					hasResult: !!result,
					resultType: typeof result
				});
				// Still emit success - payment might have succeeded
				const successResult = this.stripeCheckoutService.handlePaymentSuccess(result);
				// this.checkoutCompleted.emit(successResult);
			}
		} catch (error: unknown) {
			this.log.error('Error in handleCheckout:', error);
			const errorMsg = this.stripeCheckoutService.handlePaymentError(error);
			this.errorMessage.set(errorMsg);
			this.checkoutError.emit(error instanceof Error ? error : new Error(errorMsg));
		} finally {
			this.log.debug('Payment processing completed - isProcessingPayment set to false');
			this.isProcessingPayment.set(false);
		}
	}

	/**
	 * Ensures receipt data is set with at least basic information
	 */
	private ensureReceiptData(orderNumber?: string | null): void {
		// If receipt data is already set, only update orderNumber if it's missing
		const currentReceipt = this.receiptData();
		if (currentReceipt) {
			if (orderNumber && !currentReceipt.orderNumber) {
				this.receiptData.set({
					...currentReceipt,
					orderNumber
				});
			}
			return;
		}

		// Set basic receipt data
		this.receiptData.set({
			orderNumber: orderNumber || null,
			name: this.fullName() || '',
			billingAddress: {},
			cardLast4: undefined,
			subtotal: this.subtotal(),
			tax: this.tax(),
			total: this.total(),
			currency: this.currency().toUpperCase()
		});
	}

	/**
	 * Extracts receipt data from payment result and billing address
	 */
	private async extractReceiptData(
		result: unknown,
		elements: StripeElements,
		orderNumber?: string | null
	): Promise<void> {
		try {
			// Get billing address from Address Element
			const addressElement = elements.getElement('address');
			let billingAddress: ReceiptData['billingAddress'] = {};
			let cardLast4: string | undefined;

			if (addressElement) {
				const addressValue = await addressElement.getValue();
				if (addressValue.complete && addressValue.value.address) {
					billingAddress = {
						line1: addressValue.value.address.line1,
						line2: addressValue.value.address.line2,
						city: addressValue.value.address.city,
						state: addressValue.value.address.state,
						postal_code: addressValue.value.address.postal_code,
						country: addressValue.value.address.country
					};
				}
			}

			// Extract payment method details from result
			if (result && typeof result === 'object') {
				const resultObj = result as unknown as Record<string, unknown>;
				const paymentIntent = resultObj['paymentIntent'] as Record<string, unknown> | undefined;

				if (paymentIntent) {
					const paymentMethod = paymentIntent['payment_method'] as Record<string, unknown> | undefined;
					if (paymentMethod) {
						const card = paymentMethod['card'] as Record<string, unknown> | undefined;
						if (card && typeof card['last4'] === 'string') {
							cardLast4 = card['last4'];
						}
					}
				}

				// Also try direct access if paymentIntent is the result itself
				if (!cardLast4 && resultObj['payment_method']) {
					const paymentMethod = resultObj['payment_method'] as Record<string, unknown>;
					const card = paymentMethod['card'] as Record<string, unknown> | undefined;
					if (card && typeof card['last4'] === 'string') {
						cardLast4 = card['last4'];
					}
				}
			}

			// Get name from user data
			const name = this.fullName() || '';

			// Store receipt data
			this.receiptData.set({
				orderNumber: orderNumber || null,
				name,
				billingAddress,
				cardLast4,
				subtotal: this.subtotal(),
				tax: this.tax(),
				total: this.total(),
				currency: this.currency().toUpperCase()
			});

			this.log.debug('Receipt data extracted', {
				hasName: !!name,
				hasBillingAddress: Object.keys(billingAddress).length > 0,
				hasCardLast4: !!cardLast4
			});
		} catch (error) {
			this.log.error('Error extracting receipt data:', error);
			// Still create receipt with available data
			this.receiptData.set({
				orderNumber: orderNumber || null,
				name: this.fullName() || '',
				billingAddress: {},
				cardLast4: undefined,
				subtotal: this.subtotal(),
				tax: this.tax(),
				total: this.total(),
				currency: this.currency().toUpperCase()
			});
		}
	}

	/**
	 * Handles the "Done" button click after successful payment
	 */
	onDone(): void {
		this.checkoutCompleted.emit({ done: true });
	}
}
